import React, { useState, useEffect } from "react";
import { CellContext } from "@tanstack/react-table";
import { Interview } from "@/services/interviewService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Copy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { aiService } from "@/services/serviceFactory";

interface InterviewDescriptionCellProps
  extends CellContext<Interview, unknown> {}

export const InterviewDescriptionCell: React.FC<
  InterviewDescriptionCellProps
> = ({ getValue, row, column, table }) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState(initialValue || "");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Get the custom data from the table meta
  const meta = table.options.meta as any;

  React.useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Auto-generate description when relevant fields change
  React.useEffect(() => {
    const interview = row.original;
    if (
      interview.date &&
      interview.expertFirstName &&
      interview.expertLastName &&
      interview.anonymizedAttribution &&
      !value && // Only auto-generate if there's no existing value
      !initialValue // And no initial value from database
    ) {
      const generatedDescription = aiService.generateInterviewDescription(
        interview.date,
        interview.expertFirstName,
        interview.expertLastName,
        interview.anonymizedAttribution
      );
      setValue(generatedDescription);

      // Update the table data
      if (meta?.updateData) {
        meta.updateData(
          interview.id,
          "interviewDescription",
          generatedDescription
        );
      }
    }
  }, [
    row.original.date,
    row.original.expertFirstName,
    row.original.expertLastName,
    row.original.anonymizedAttribution,
    value,
    initialValue, // Add initialValue to dependencies
    meta,
    row.original.id,
  ]);

  const handleRefresh = () => {
    const interview = row.original;

    if (
      !interview.date ||
      !interview.expertFirstName ||
      !interview.expertLastName ||
      !interview.anonymizedAttribution
    ) {
      toast({
        title: "Missing Information",
        description:
          "Date, names, and anonymized attribution are required to generate description.",
        variant: "destructive",
      });
      return;
    }

    const generatedDescription = aiService.generateInterviewDescription(
      interview.date,
      interview.expertFirstName,
      interview.expertLastName,
      interview.anonymizedAttribution
    );

    setValue(generatedDescription);

    // Update the table data
    if (meta?.updateData) {
      meta.updateData(
        interview.id,
        "interviewDescription",
        generatedDescription
      );
    }

    toast({
      title: "Success",
      description: "Interview description refreshed!",
    });
  };

  const handleCopy = async () => {
    if (value) {
      try {
        await navigator.clipboard.writeText(value);
        toast({
          title: "Copied",
          description: "Interview description copied to clipboard!",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to copy to clipboard.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSave = () => {
    if (meta?.updateData) {
      meta.updateData(row.original.id, "interviewDescription", value);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Save the current edit
      handleSave();

      // Prevent default Enter behavior to avoid form submission
      e.preventDefault();

      // Get all visible rows
      const rows = table.getRowModel().rows;
      const currentRowIndex = rows.findIndex((r) => r.id === row.id);
      const nextRowIndex = currentRowIndex + 1;

      // If there's a next row, focus the same column in the next row
      if (nextRowIndex < rows.length) {
        const nextRowId = rows[nextRowIndex].original.id;

        setTimeout(() => {
          // Use document query to find the cell directly in the DOM
          const targetCell = document.querySelector(
            `[data-cell-id="${nextRowId}:interviewDescription"]`
          );
          if (targetCell) {
            (targetCell as HTMLElement).click();
          }
        }, 50);
      }
    } else if (e.key === "Escape") {
      setValue(initialValue || "");
      setIsEditing(false);
    } else if (e.key === "Tab") {
      // Prevent default tab behavior
      e.preventDefault();

      // Save the current edit
      handleSave();

      // Get all visible columns that can be edited (excluding the select column and non-editable columns)
      const visibleColumns = table
        .getVisibleLeafColumns()
        .filter((col) => col.id !== "select" && col.id !== "totalCost");

      // Find the index of the current column
      const currentColIndex = visibleColumns.findIndex(
        (col) => col.id === "interviewDescription"
      );

      // Determine the next column based on shift key
      const nextColIndex = e.shiftKey
        ? currentColIndex - 1
        : currentColIndex + 1;

      // Target information for next cell
      let targetRowId = row.original.id;
      let targetColId = "";

      // Handle going to the next row if we're at the end of columns
      if (nextColIndex >= visibleColumns.length && !e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const nextRowIndex = currentRowIndex + 1;

        if (nextRowIndex < rows.length) {
          // Go to first column of next row
          targetRowId = rows[nextRowIndex].original.id;
          targetColId = visibleColumns[0].id;
        } else {
          // Stay in the current cell if we're at the last cell of the table
          targetRowId = row.original.id;
          targetColId = "interviewDescription";
        }
      }
      // Handle going to previous row if we're at the beginning of columns
      else if (nextColIndex < 0 && e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const prevRowIndex = currentRowIndex - 1;

        if (prevRowIndex >= 0) {
          // Go to last column of previous row
          targetRowId = rows[prevRowIndex].original.id;
          targetColId = visibleColumns[visibleColumns.length - 1].id;
        } else {
          // Stay in the current cell if we're at the first cell of the table
          targetRowId = row.original.id;
          targetColId = "interviewDescription";
        }
      }
      // Standard next/prev column in same row
      else if (nextColIndex >= 0 && nextColIndex < visibleColumns.length) {
        targetColId = visibleColumns[nextColIndex].id;
      } else {
        // No valid next cell
        targetRowId = row.original.id;
        targetColId = "interviewDescription";
      }

      // Use setTimeout to ensure the current edit is completely finished
      setTimeout(() => {
        const targetCell = document.querySelector(
          `[data-cell-id="${targetRowId}:${targetColId}"]`
        );
        if (targetCell) {
          (targetCell as HTMLElement).click();
        }
      }, 50);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className="flex-1 h-8 focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder="Enter interview description..."
        />
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center w-full min-h-[32px] group"
      data-cell-id={`${row.original.id}:interviewDescription`}
      onClick={() => setIsEditing(true)}
    >
      <div
        className="flex-1 cursor-pointer flex items-center text-sm truncate pr-1"
        title={value}
      >
        {value || (
          <span className="text-gray-400 dark:text-gray-500 italic">
            Auto-generated when fields are filled
          </span>
        )}
      </div>

      {/* Overlay buttons on hover with gradient background */}
      <div className="absolute right-0 top-0 bottom-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-gradient-to-l from-gray-50/90 dark:from-gray-700/90 via-gray-50/60 dark:via-gray-700/60 to-transparent pl-4 pr-1">
        {value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="h-7 w-7 p-0 hover:bg-gray-200 dark:hover:bg-gray-600 shadow-sm bg-gray-100 dark:bg-gray-700"
            title="Copy to clipboard"
          >
            <Copy className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRefresh();
          }}
          className="h-7 w-7 p-0 hover:bg-gray-200 dark:hover:bg-gray-600 shadow-sm bg-gray-100 dark:bg-gray-700"
          title="Refresh Description"
        >
          <RefreshCw className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
        </Button>
      </div>
    </div>
  );
};
